/**
 * Embedding Utilities
 * Helper functions for working with embedding vectors
 */

import { generateEmbedding, getItemEmbedding as generateItemEmbedding } from '../services/embeddingService';

// In-memory cache for item embeddings (to avoid regenerating)
const ITEM_EMBEDDING_CACHE = new Map();

/**
 * Calculate cosine similarity between two vectors
 * @param {number[]} a - First vector
 * @param {number[]} b - Second vector
 * @returns {number} - Similarity score between -1 and 1
 */
export function cosineSimilarity(a, b) {
  if (!a || !b || !Array.isArray(a) || !Array.isArray(b)) {
    return 0;
  }
  
  if (a.length !== b.length || a.length === 0) {
    return 0;
  }
  
  let dotProduct = 0;
  let normA = 0;
  let normB = 0;
  
  for (let i = 0; i < a.length; i++) {
    const aVal = Number(a[i]) || 0;
    const bVal = Number(b[i]) || 0;
    dotProduct += aVal * bVal;
    normA += aVal * aVal;
    normB += bVal * bVal;
  }
  
  normA = Math.sqrt(normA);
  normB = Math.sqrt(normB);
  
  if (normA === 0 || normB === 0) {
    return 0;
  }
  
  return dotProduct / (normA * normB);
}

/**
 * Safely extract string from a value
 */
function safeString(value) {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  return '';
}

/**
 * Get embedding for an item, using cached value or generating new one
 * @param {Object} item - Item object with potential embedding
 * @returns {Promise<number[]|null>} - Embedding vector or null
 */
export async function getItemEmbedding(item) {
  if (!item) return null;
  
  // Check if item already has embedding stored
  if (item.embedding && Array.isArray(item.embedding) && item.embedding.length > 0) {
    return item.embedding;
  }
  
  // Build cache key safely
  const titlePart = safeString(item.title);
  const descPart = safeString(item.description);
  const cacheKey = item.id || `${titlePart}-${descPart}`.slice(0, 100);
  
  // Check memory cache
  if (cacheKey && ITEM_EMBEDDING_CACHE.has(cacheKey)) {
    return ITEM_EMBEDDING_CACHE.get(cacheKey);
  }
  
  // Use the unified embedding service which has proper validation
  try {
    const embedding = await generateItemEmbedding(item);
    
    if (embedding && Array.isArray(embedding) && embedding.length > 0) {
      // Cache in memory only (no DB write)
      if (cacheKey) {
        ITEM_EMBEDDING_CACHE.set(cacheKey, embedding);
      }
      return embedding;
    }
    
    return null;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('[EmbeddingUtils] Failed to generate item embedding:', error);
    }
    return null;
  }
}

/**
 * Clear the item embedding cache
 */
export function clearEmbeddingCache() {
  ITEM_EMBEDDING_CACHE.clear();
}

/**
 * Get cache stats for debugging
 */
export function getEmbeddingCacheStats() {
  return {
    size: ITEM_EMBEDDING_CACHE.size,
    keys: Array.from(ITEM_EMBEDDING_CACHE.keys()).slice(0, 10)
  };
}
